import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, BrainCircuit, Megaphone, Target, TrendingUp, Inbox, Loader2,
  CheckCircle2, Flag, Sparkles, RefreshCw, Sunrise, Mic, Play,
} from "lucide-react";
import { jarvisApi, voiceApi } from "@/lib/api-client";
import { toast } from "sonner";

const TABS = [
  { id: "brief", label: "Morning Brief", icon: Sunrise },
  { id: "digest", label: "Proactive Digest", icon: Sparkles },
  { id: "broadcast", label: "Broadcast Engine", icon: Megaphone },
  { id: "warroom", label: "Campaign War Room", icon: Target },
  { id: "revenue", label: "Revenue Intelligence", icon: TrendingUp },
  { id: "envelopes", label: "Agent Envelopes", icon: Inbox },
  { id: "voice", label: "Voice Telemetry", icon: Mic },
];

export default function JarvisBridge() {
  const [params] = useSearchParams();
  const initial = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "brief";
  const [tab, setTab] = useState(initial);
  const [session, setSession] = useState(null);

  useEffect(() => { jarvisApi.session().then(setSession).catch(() => {}); }, []);

  return (
    <div className="min-h-screen uc-grain relative" data-testid="jarvis-bridge-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="uc-chip flex items-center gap-1.5 hover:bg-white/5" data-testid="jarvis-back-btn">
              <ArrowLeft size={11} /> Dashboard
            </Link>
            <div>
              <div className="font-mono-uc text-[9px] uppercase tracking-[0.3em] text-sky-300/80">REASONING &amp; MEMORY INFRASTRUCTURE</div>
              <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
                <BrainCircuit size={20} className="text-sky-300" /> Oracle Command Bridge
              </h1>
            </div>
          </div>
          {session && (
            <span className="uc-chip font-mono-uc text-[10px] uppercase text-emerald-300 border-emerald-500/40"
              data-testid="jarvis-rehydrated-chip">
              {session.rehydrated ? `Memory rehydrated · ${session.turns} turns` : "Fresh session · durable store armed"}
            </span>
          )}
        </div>

        <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto uc-scroll">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 font-mono-uc text-[10px] uppercase tracking-widest flex items-center gap-1.5 border-b-2 -mb-px transition-colors whitespace-nowrap ${
                tab === t.id ? "border-sky-400 text-sky-300" : "border-transparent text-white/40 hover:text-white/70"}`}
              data-testid={`jarvis-tab-${t.id}`}>
              <t.icon size={11} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "brief" && <MorningBrief />}
        {tab === "digest" && <ProactiveDigest />}
        {tab === "broadcast" && <BroadcastEngine />}
        {tab === "warroom" && <CampaignWarRoom />}
        {tab === "revenue" && <RevenueIntel />}
        {tab === "envelopes" && <Envelopes />}
        {tab === "voice" && <VoiceTelemetry />}
      </div>
    </div>
  );
}

const playB64 = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Audio(URL.createObjectURL(new Blob([bytes], { type: "audio/mpeg" }))).play();
};

function MorningBrief() {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = (force) => voiceApi.brief(force).then(setBrief).catch(() => {});
  useEffect(() => { load(); }, []);
  const play = async () => {
    setBusy(true);
    try {
      const r = await voiceApi.briefAudio();
      await playB64(r.audio_b64);
      voiceApi.briefPlayed().catch(() => {});
      toast.success(`Brief spoken — ${r.cached ? "cached audio" : `synthesized in ${r.tts_ms}ms`}`);
    } catch { toast.error("Brief audio failed"); } finally { setBusy(false); }
  };
  if (!brief) return <Loader2 className="animate-spin text-amber-400" />;
  return (
    <div data-testid="morning-brief-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-white/50 text-xs">
          One spoken-style digest per day — every line backed by episode and approval records.
        </div>
        <div className="flex gap-2">
          <button onClick={play} disabled={busy}
            className="uc-chip text-amber-400 hover:bg-amber-500/10 flex items-center gap-1.5"
            data-testid="brief-play-btn">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />} Play Brief
          </button>
          <button onClick={() => load(true)} className="uc-chip text-white/50 flex items-center gap-1.5"
            data-testid="brief-regenerate-btn">
            <RefreshCw size={11} /> Regenerate
          </button>
        </div>
      </div>
      <div className="border border-amber-500/30 bg-amber-500/5 p-5 mb-4" data-testid="brief-spoken-text">
        <div className="font-mono-uc text-[9px] uppercase text-amber-400 mb-2">
          Oracle · {brief.date} {brief.played_at ? "· played" : ""}
        </div>
        <div className="text-sm text-white/90 leading-relaxed">{brief.spoken_text}</div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Overnight episodes", brief.episodes_count], ["Awaiting signature", (brief.signature_items || []).length],
          ["Revenue today", `$${Number(brief.revenue_today || 0).toLocaleString()}`],
          ["Active ventures", brief.active_ventures]].map(([k, v]) => (
          <div key={k} className="border border-white/10 bg-black/40 p-3">
            <div className="font-mono-uc text-[9px] uppercase text-white/40">{k}</div>
            <div className="text-lg text-white mt-1">{v}</div>
          </div>
        ))}
      </div>
      {(brief.signature_items || []).length > 0 && (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="brief-signature-items">
          <div className="font-mono-uc text-[9px] uppercase text-rose-300 mb-2">Signature queue</div>
          {brief.signature_items.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-white/70 py-1 border-b border-white/5 last:border-0">
              <span className={`font-mono-uc text-[9px] uppercase px-1 border ${s.tier === "T3" ? "text-rose-300 border-rose-500/40" : "text-amber-400 border-amber-500/40"}`}>{s.tier}</span>
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VoiceTelemetry() {
  const [data, setData] = useState(null);
  const load = () => voiceApi.turns().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <Loader2 className="animate-spin text-sky-300" />;
  const m = data.metrics;
  const GATE_CLS = { PASS: "text-emerald-300 border-emerald-500/40", DEGRADED: "text-amber-400 border-amber-500/40",
    BROKEN: "text-rose-300 border-rose-500/40", NO_DATA: "text-white/40 border-white/20" };
  return (
    <div data-testid="voice-telemetry-panel">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className={`uc-chip font-mono-uc text-[10px] uppercase ${GATE_CLS[m.gate]}`} data-testid="voice-gate-badge">
          Gate: {m.gate} · basis {m.gate_basis}
        </span>
        <span className="font-mono-uc text-[10px] text-white/50" data-testid="voice-p95-m2e">
          P95 mouth-to-ear: {m.p95_mouth_to_ear_ms ?? "—"}ms (target &lt;{m.p95_target_ms}ms · broken &gt;{m.broken_ms}ms)
        </span>
        <span className="font-mono-uc text-[10px] text-white/50" data-testid="voice-p95-server">
          P95 server: {m.p95_server_total_ms ?? "—"}ms · {m.samples} turns
        </span>
        <button onClick={load} className="uc-chip text-sky-300 ml-auto" data-testid="voice-refresh-btn"><RefreshCw size={11} /></button>
      </div>
      <div className="border border-white/10 bg-black/40 overflow-x-auto uc-scroll">
        <table className="w-full text-xs">
          <thead>
            <tr className="font-mono-uc text-[9px] uppercase text-white/40 border-b border-white/10">
              {["When", "Transcript", "Intent", "Ear ms", "Full ms", "Server ms", "TTS ms", "Cost $", "Tools"].map((h) => (
                <th key={h} className="text-left px-3 py-2">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {data.turns.map((t) => (
              <tr key={t.id} className="border-b border-white/5 text-white/70" data-testid={`voice-turn-${t.id}`}>
                <td className="px-3 py-1.5 whitespace-nowrap">{(t.created_at || "").slice(11, 19)}</td>
                <td className="px-3 py-1.5 max-w-[220px] truncate">{t.transcript}</td>
                <td className="px-3 py-1.5 font-mono-uc text-[9px] uppercase text-sky-300">{t.intent}</td>
                <td className={`px-3 py-1.5 ${t.mouth_to_ear_ms > 1000 ? "text-rose-300" : "text-emerald-300"}`}>{t.mouth_to_ear_ms ?? "—"}</td>
                <td className="px-3 py-1.5">{t.full_response_ms ?? "—"}</td>
                <td className="px-3 py-1.5">{Math.round(t.server_total_ms)}</td>
                <td className="px-3 py-1.5">{Math.round(t.tts_ms)}{t.tts_cached ? " ⚡" : ""}</td>
                <td className="px-3 py-1.5">{t.cost_estimate}</td>
                <td className="px-3 py-1.5 font-mono-uc text-[9px] text-white/40">{(t.tools_used || []).join(", ") || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.turns.length === 0 && (
          <div className="p-6 text-center text-white/40 text-sm" data-testid="voice-turns-empty">
            No voice turns yet. Arm the voice layer in the Boardroom and say: Command, status.
          </div>
        )}
      </div>
    </div>
  );
}

function ProactiveDigest() {
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { jarvisApi.proactiveDigest().then(setDigest).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const generate = async () => {
    setBusy(true);
    try { const r = await jarvisApi.proactiveGenerate(); toast.info(`${r.generated} item(s) surfaced`); load(); }
    catch { toast.error("Generation failed"); } finally { setBusy(false); }
  };
  const respond = async (id, action) => {
    await jarvisApi.proactiveRespond(id, action); load();
  };
  const CLS = { alert: "text-rose-300 border-rose-500/40", suggestion: "text-white/60 border-white/20",
    automation_proposal: "text-amber-400 border-amber-500/40" };
  return (
    <div data-testid="proactive-digest-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-white/50 text-xs">One digest, not a drip. Max 3/day per venture · every item cites its source record.</div>
        <button onClick={generate} disabled={busy} className="uc-chip hover:bg-sky-500/10 text-sky-300 flex items-center gap-1.5"
          data-testid="proactive-generate-btn">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Scan Now
        </button>
      </div>
      {!digest ? <Loader2 className="animate-spin text-sky-300" /> : digest.digest.length === 0 ? (
        <div className="border border-white/10 p-8 text-center text-white/40 text-sm" data-testid="digest-empty">
          Nothing surfaced today. The Oracle stays silent without a source record.
        </div>
      ) : (
        <div className="space-y-2">
          {digest.digest.map((i) => (
            <div key={i.id} className={`border bg-black/40 p-4 ${CLS[i.class] || "border-white/10"}`}
              data-testid={`proactive-item-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="font-mono-uc text-[9px] uppercase mr-2">{i.class}</span>
                  <span className="text-sm text-white">{i.title}</span>
                </div>
                <div className="flex gap-2">
                  {i.status === "surfaced" && (<>
                    <button onClick={() => respond(i.id, "engaged")} className="uc-chip text-emerald-300" data-testid={`engage-${i.id}`}>Engage</button>
                    <button onClick={() => respond(i.id, "dismissed")} className="uc-chip text-white/40" data-testid={`dismiss-${i.id}`}>Dismiss</button>
                  </>)}
                  {i.status !== "surfaced" && <span className="font-mono-uc text-[9px] uppercase text-white/40">{i.status}</span>}
                </div>
              </div>
              <div className="text-xs text-white/60 mt-1">{i.body}</div>
              <div className="font-mono-uc text-[9px] text-sky-300/70 mt-1" data-testid={`source-ref-${i.id}`}>source: {i.source_ref} · score {i.score}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BroadcastEngine() {
  const [brief, setBrief] = useState("");
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => { jarvisApi.broadcasts().then((r) => setPacks(r.packs)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const draft = async () => {
    if (!brief.trim()) return;
    setBusy(true);
    try { await jarvisApi.broadcastDraft({ brief }); setBrief(""); toast.success("Publish Pack drafted — awaiting your approval"); load(); }
    catch { toast.error("Draft failed"); } finally { setBusy(false); }
  };
  const approve = async (id) => {
    try { const r = await jarvisApi.broadcastApprove(id); toast.success(`Published — artifact sealed (${r.vault_artifact?.slice(0, 11)}…)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
  };
  return (
    <div data-testid="broadcast-engine-panel">
      <div className="border border-white/10 bg-black/40 p-4 mb-5">
        <div className="font-mono-uc text-[10px] uppercase text-white/50 mb-2">Command, publish — content brief</div>
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
          placeholder="Content brief: what should the estate broadcast?"
          className="w-full bg-black/60 border border-white/15 p-3 text-sm text-white focus:border-sky-500/50 outline-none"
          data-testid="broadcast-brief-input" />
        <button onClick={draft} disabled={busy || !brief.trim()}
          className="uc-chip mt-2 hover:bg-sky-500/10 text-sky-300 flex items-center gap-1.5 disabled:opacity-40"
          data-testid="broadcast-draft-btn">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Megaphone size={11} />} Draft Publish Pack (Creator → Executor)
        </button>
      </div>
      <div className="space-y-3">
        {packs.map((p) => (
          <div key={p.id} className="border border-white/10 bg-black/40 p-4" data-testid={`publish-pack-${p.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-sm text-white truncate max-w-[60%]">{p.brief}</div>
              <div className="flex items-center gap-2">
                <span className="uc-chip font-mono-uc text-[9px] uppercase text-amber-400">T2 · Sentinel gate</span>
                <span className={`font-mono-uc text-[9px] uppercase ${p.status === "published" ? "text-emerald-300" : "text-amber-400"}`}>{p.status}</span>
                {p.status === "awaiting_approval" && (
                  <button onClick={() => approve(p.id)} className="uc-chip text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`approve-pack-${p.id}`}>
                    <CheckCircle2 size={11} className="inline mr-1" />Approve &amp; Publish
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {Object.entries(p.drafts || {}).map(([plat, d]) => (
                <div key={plat} className="border border-white/5 bg-black/50 p-2">
                  <div className="font-mono-uc text-[9px] uppercase text-sky-300">{plat}</div>
                  <div className="text-xs text-white/70 whitespace-pre-wrap max-h-24 overflow-y-auto uc-scroll">{d.copy}</div>
                  <div className="text-[10px] text-white/40 mt-1">{(d.hashtags || []).join(" ")} · {d.image_spec}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CampaignWarRoom() {
  const [form, setForm] = useState({ name: "", budget: "", audience: "", objective: "", creative_brief: "" });
  const [camps, setCamps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({});
  const load = () => { jarvisApi.campaigns().then((r) => setCamps(r.campaigns)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    setBusy(true);
    try {
      await jarvisApi.campaignCreate({ ...form, budget: parseFloat(form.budget) });
      setForm({ name: "", budget: "", audience: "", objective: "", creative_brief: "" });
      toast.success("Controller produced the campaign brief — T3 approval pending"); load();
    } catch (e) { toast.error("Brief generation failed"); } finally { setBusy(false); }
  };
  const approve = async (id) => {
    try { const r = await jarvisApi.campaignApprove(id); toast.success("Spend commitment approved — episode recorded"); load(); }
    catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Approval failed"); }
  };
  const logResults = async (id) => {
    const r = results[id] || {};
    try {
      const res = await jarvisApi.campaignResults(id, { spend: parseFloat(r.spend || 0), revenue: parseFloat(r.revenue || 0) });
      toast[res.decision_gate ? "error" : "success"](
        res.decision_gate ? `ROAS ${res.roas} < 1.0 — routed to Sentinel Decision Gate` : `ROAS ${res.roas} logged`);
      load();
    } catch (e) { toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
  };
  const F = (k, ph, type = "text") => (
    <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} type={type}
      className="bg-black/60 border border-white/15 p-2 text-xs text-white focus:border-amber-500/50 outline-none w-full"
      data-testid={`campaign-${k}-input`} />
  );
  return (
    <div data-testid="war-room-panel">
      <div className="border border-white/10 bg-black/40 p-4 mb-5 grid grid-cols-1 md:grid-cols-2 gap-2">
        {F("name", "Campaign name")}{F("budget", "Budget USD", "number")}
        {F("audience", "Audience description")}{F("objective", "Objective")}
        <div className="md:col-span-2">{F("creative_brief", "Creative brief")}</div>
        <button onClick={create} disabled={busy || !form.name || !form.budget}
          className="uc-chip md:col-span-2 justify-center hover:bg-amber-500/10 text-amber-400 disabled:opacity-40"
          data-testid="campaign-create-btn">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Target size={11} className="mr-1" />} Generate Launch Brief (Controller)
        </button>
      </div>
      <div className="space-y-3">
        {camps.map((c) => (
          <div key={c.id} className="border border-white/10 bg-black/40 p-4" data-testid={`campaign-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm text-white">{c.name} · ${c.budget?.toLocaleString()}</div>
              <div className="flex items-center gap-2">
                <span className="uc-chip font-mono-uc text-[9px] uppercase text-rose-300">T3</span>
                <span className="font-mono-uc text-[9px] uppercase text-white/50">proj ROAS {c.projected_roas}x</span>
                {c.roas != null && <span className={`font-mono-uc text-[9px] uppercase ${c.roas < 1 ? "text-rose-300" : "text-emerald-300"}`}>actual {c.roas}x</span>}
                <span className="font-mono-uc text-[9px] uppercase text-amber-400">{c.status}</span>
              </div>
            </div>
            {c.status === "awaiting_approval" && (
              <div className="mt-2 border border-rose-500/30 bg-rose-500/5 p-3">
                <div className="font-mono-uc text-[9px] uppercase text-rose-300 mb-1">Third gate — plain language</div>
                <div className="text-xs text-white/80" data-testid={`plain-statement-${c.id}`}>{c.plain_statement}</div>
                <button onClick={() => approve(c.id)} className="uc-chip mt-2 text-emerald-300 hover:bg-emerald-500/10"
                  data-testid={`approve-campaign-${c.id}`}>Approve Spend Commitment</button>
              </div>
            )}
            {c.status === "approved_committed" && (
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <input placeholder="Actual spend" type="number" className="bg-black/60 border border-white/15 p-1.5 text-xs text-white w-28"
                  onChange={(e) => setResults({ ...results, [c.id]: { ...(results[c.id] || {}), spend: e.target.value } })}
                  data-testid={`spend-input-${c.id}`} />
                <input placeholder="Revenue" type="number" className="bg-black/60 border border-white/15 p-1.5 text-xs text-white w-28"
                  onChange={(e) => setResults({ ...results, [c.id]: { ...(results[c.id] || {}), revenue: e.target.value } })}
                  data-testid={`revenue-input-${c.id}`} />
                <button onClick={() => logResults(c.id)} className="uc-chip text-sky-300" data-testid={`log-results-${c.id}`}>Log Results → Controller</button>
              </div>
            )}
            <details className="mt-2">
              <summary className="font-mono-uc text-[9px] uppercase text-white/40 cursor-pointer">Launch brief</summary>
              <pre className="text-[10px] text-white/60 whitespace-pre-wrap mt-1">{c.brief}</pre>
            </details>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenueIntel() {
  const [data, setData] = useState(null);
  useEffect(() => { jarvisApi.revenueIntel().then(setData).catch(() => {}); }, []);
  if (!data) return <Loader2 className="animate-spin text-emerald-300" />;
  return (
    <div data-testid="revenue-intel-panel">
      <div className="border border-sky-500/30 bg-sky-500/5 p-3 mb-4 text-xs text-white/80" data-testid="jarvis-intel-summary">
        <span className="font-mono-uc text-[9px] uppercase text-sky-300 mr-2">Oracle</span>{data.jarvis_summary}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.ventures.map((v) => (
          <div key={v.venture_id} className="border border-white/10 bg-black/40 p-4" data-testid={`intel-${v.venture_id}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm text-white">{v.venture}</div>
              <div className="font-mono-uc text-[9px] uppercase text-white/40">{v.episodes_7d} episodes · 7d</div>
            </div>
            <div className="flex gap-4 mt-2 text-xs">
              <span className="text-white/60">Spend <span className="text-white">${v.ad_spend.toLocaleString()}</span></span>
              <span className="text-white/60">Revenue <span className="text-emerald-300">${v.revenue.toLocaleString()}</span></span>
              <span className="text-white/60">ROAS <span className={v.roas != null && v.roas < 1 ? "text-rose-300" : "text-amber-400"}>{v.roas ?? "—"}</span></span>
              <span className="text-white/60">Reasoning cost <span className="text-white">${v.reasoning_cost_7d}</span></span>
            </div>
            <div className="flex items-end gap-1 h-10 mt-3" data-testid={`trend-${v.venture_id}`}>
              {v.trend_7d.map((t) => (
                <div key={t.day} title={`${t.day}: ${t.episodes}`}
                  className="flex-1 bg-sky-400/40"
                  style={{ height: `${Math.min(100, t.episodes * 20 + 4)}%` }} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Envelopes() {
  const [data, setData] = useState(null);
  const [agent, setAgent] = useState("");
  const [flags, setFlags] = useState([]);
  useEffect(() => {
    jarvisApi.envelopes(agent ? { agent } : {}).then(setData).catch(() => {});
    jarvisApi.flags().then((r) => setFlags(r.flags)).catch(() => {});
  }, [agent]);
  const AGENTS = ["", "scout", "sentinel", "executor", "auditor", "controller", "creator", "oracle"];
  return (
    <div data-testid="envelopes-panel">
      <div className="flex items-center gap-2 mb-4">
        {AGENTS.map((a) => (
          <button key={a || "all"} onClick={() => setAgent(a)}
            className={`uc-chip font-mono-uc text-[9px] uppercase ${agent === a ? "text-sky-300 border-sky-500/50" : "text-white/40"}`}
            data-testid={`envelope-filter-${a || "all"}`}>{a || "all"}</button>
        ))}
        <span className="ml-auto font-mono-uc text-[9px] uppercase text-white/40">read + flag only · zero write access</span>
      </div>
      {flags.length > 0 && (
        <div className="border border-rose-500/30 bg-rose-500/5 p-3 mb-3" data-testid="jarvis-flags-strip">
          <div className="font-mono-uc text-[9px] uppercase text-rose-300 mb-1 flex items-center gap-1"><Flag size={10} /> Raised flags</div>
          {flags.slice(0, 3).map((f) => (
            <div key={f.id} className="text-xs text-white/70">{f.agent}: {f.reason?.slice(0, 120)}</div>
          ))}
        </div>
      )}
      {!data ? <Loader2 className="animate-spin text-sky-300" /> : (
        <div className="space-y-2">
          {data.envelopes.slice(0, 40).map((e, i) => (
            <div key={i} className="border border-white/10 bg-black/40 p-3 flex flex-wrap items-center justify-between gap-2"
              data-testid={`envelope-row-${i}`}>
              <div className="min-w-0">
                <span className="font-mono-uc text-[9px] uppercase text-amber-400 mr-2">{e.agent}</span>
                <span className="font-mono-uc text-[9px] uppercase text-white/40 mr-2">{e.artifact_type}</span>
                <span className="text-xs text-white/80">{(e.payload?.title || JSON.stringify(e.payload || {})).slice(0, 110)}</span>
              </div>
              <div className="font-mono-uc text-[9px] text-white/40">conf {e.confidence} · {(e.created_at || "").slice(0, 10)}{e.derived ? " · derived" : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
